/**
 * @file: useEntitledAiHomeTools.ts
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import {
  AiSuiteHomeTool,
  kAiSuiteTools,
} from '@euler/app/flows/ai-suite/theme';
import { useStoreFeatureLookup } from '@euler/functions/entitlements/useStoreFeature';
import { AiHomeFeature } from '@euler/model/ai-home';
import { StoreFeature } from '@euler/model/enum';
import { useMemo } from 'react';

/**
 * The feature a tool needs. 经营分析 needs none of its own: without BI it
 * opens the assistant's analytics chat.
 */
const kToolFeatures: Record<AiHomeFeature, StoreFeature | null> = {
  [AiHomeFeature.Assistant]: null,
  [AiHomeFeature.Apdm]: StoreFeature.Apdm,
  [AiHomeFeature.Coaching]: StoreFeature.Coaching,
  [AiHomeFeature.BusinessIntelligence]: null,
  [AiHomeFeature.Posters]: StoreFeature.PosterStudio,
};

export interface EntitledAiHomeTool {
  tool: AiSuiteHomeTool;
  /** Held only through a trial: when it ends. */
  trialEndsAt: string | null;
}

/** The AI home's tools the store has. */
export function useEntitledAiHomeTools(): EntitledAiHomeTool[] {
  const featureOf = useStoreFeatureLookup();
  return useMemo(
    () =>
      kAiSuiteTools.flatMap(tool => {
        const feature = kToolFeatures[tool.feature];
        if (feature == null) return [{ tool, trialEndsAt: null }];
        const access = featureOf(feature);
        return access.enabled
          ? [{ tool, trialEndsAt: access.trialEndsAt }]
          : [];
      }),
    [featureOf],
  );
}
